import React, { useState, useEffect } from 'react';
import { getData, postData, putData, deleteData, API_BASE_URL, API_TOKEN } from '../helpers/ApiSettings';
import ProductTable from '../components/Products/ProductTable';
import AddProductModal from '../components/Products/AddProductModal';
import EditProductModal from '../components/Products/EditProductModal';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize] = useState(8); // Items per page

  const [newProduct, setNewProduct] = useState({
    titre: '',
    description: '',
    reference: '',
    daily_price: '',
    caution: '',
    quantity: 1,
    available: true,
    category: '',
    price: []
  });

  const fetchProducts = async (page = currentPage) => {
    try {
      setLoading(true);
      // Add pagination parameters to the API call
      const response = await getData(`/products?populate=*&pagination[page]=${page}&pagination[pageSize]=${pageSize}&sort[0]=id:desc`);
      setProducts(response.data.data);
      // Calculate total pages from meta data
      const total = response.data.meta.pagination.total;
      setTotalPages(Math.ceil(total / pageSize));
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des produits');
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await getData('/categories');
      setCategories(response.data.data);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, [currentPage]); // Re-fetch when page changes

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo(0, 0); // Scroll to top when page changes
  };

  // Pagination component
  const Pagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    // Adjust start page if we're near the end
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Previous button
    pages.push(
      <li key="prev" className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
        <button
          className="page-link"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          &laquo;
        </button>
      </li>
    );

    // First page if not visible
    if (startPage > 1) {
      pages.push(
        <li key={1} className="page-item">
          <button className="page-link" onClick={() => handlePageChange(1)}>1</button>
        </li>
      );
      if (startPage > 2) {
        pages.push(<li key="ellipsis1" className="page-item disabled"><span className="page-link">...</span></li>);
      }
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
          <button className="page-link" onClick={() => handlePageChange(i)}>{i}</button>
        </li>
      );
    }

    // Last page if not visible
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<li key="ellipsis2" className="page-item disabled"><span className="page-link">...</span></li>);
      }
      pages.push(
        <li key={totalPages} className="page-item">
          <button className="page-link" onClick={() => handlePageChange(totalPages)}>{totalPages}</button>
        </li>
      );
    }

    // Next button
    pages.push(
      <li key="next" className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
        <button
          className="page-link"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          &raquo;
        </button>
      </li>
    );

    return (
      <nav aria-label="Product navigation" className="mt-4">
        <ul className="pagination justify-content-center">
          {pages}
        </ul>
      </nav>
    );
  };

  // Rest of the existing functions...
  const handleAddProduct = async (e, productData) => {
    e.preventDefault();
    try {
      const { files, ...productDetails } = productData;
      const response = await postData('/products', {
        data: productDetails
      });

      const newProductId = response.data.data.id;

      if (files && files.length > 0) {
        await handleImageUpload(newProductId, files);
      }

      await fetchProducts(currentPage);
      setNewProduct({
        titre: '',
        description: '',
        reference: '',
        daily_price: '',
        caution: '',
        quantity: 1,
        available: true,
        category: '',
        price: []
      });

      window.$('#addProductModal').modal('hide');
      document.querySelector('.modal-backdrop')?.remove();
      alert('Produit ajouté avec succès');
    } catch (err) {
      alert('Erreur lors de l\'ajout du produit');
      console.error('Error adding product:', err);
    }
  };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    try {
      const cleanPrices = editingProduct.price.map(({ id: priceId, ...priceData }) => ({
        nb_days: priceData.nb_days,
        price: priceData.price
      }));

      const updateData = {
        titre: editingProduct.titre,
        description: editingProduct.description,
        reference: editingProduct.reference,
        daily_price: editingProduct.daily_price,
        caution: editingProduct.caution,
        quantity: editingProduct.quantity,
        available: editingProduct.available,
        category: typeof editingProduct.category === 'object' ? editingProduct.category.id : editingProduct.category,
        price: cleanPrices
      };

      await putData(`/products/${editingProduct.documentId}`, {
        data: updateData
      });

      if (editingProduct.newImages && editingProduct.newImages.length > 0) {
        await handleImageUpload(editingProduct.id, editingProduct.newImages);
      }

      await fetchProducts(currentPage);
      window.$('#editProductModal').modal('hide');
      document.querySelector('.modal-backdrop')?.remove();
      setEditingProduct(null);
      alert('Produit mis à jour avec succès');
    } catch (err) {
      console.error('Error updating product:', err.response?.data || err);
      alert('Erreur lors de la mise à jour du produit: ' + (err.response?.data?.error?.message || err.message));
    }
  };

  const handleDeleteProduct = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) {
      try {
        await deleteData(`/products/${id}`);
        await fetchProducts(currentPage);
        alert('Produit supprimé avec succès');
      } catch (err) {
        alert('Erreur lors de la suppression du produit');
        console.error('Error deleting product:', err);
      }
    }
  };

  const handleEditClick = (product) => {
    const productCopy = JSON.parse(JSON.stringify(product));
    setEditingProduct(productCopy);
    window.$('#editProductModal').modal('show');
  };

  const handleImageUpload = async (productId, files) => {
    try {
      const formData = new FormData();
      formData.append('ref', 'api::product.product');
      formData.append('refId', productId);
      formData.append('field', 'images');

      files.forEach(file => {
        formData.append('files', file);
      });

      const response = await fetch(`${API_BASE_URL}/api/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${API_TOKEN}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Upload failed');
      }

      await fetchProducts(currentPage);
    } catch (error) {
      console.error('Error uploading images:', error);
      alert('Erreur lors du téléchargement des images');
      throw error;
    }
  };

  const handleImageDelete = async (productId, imageId) => {
    try {
      await deleteData(`/upload/files/${imageId}`);
  
      const updatedProduct = {
        ...editingProduct,
        images: editingProduct.images.filter(img => img.id !== imageId)
      };
  
      setEditingProduct(updatedProduct);
      setProducts(prevProducts => 
        prevProducts.map(p => p.id === productId ? {
          ...p,
          images: updatedProduct.images
        } : p)
      );
    } catch (error) {
      console.error('Error handling image deletion:', error);
      alert('Erreur lors de la suppression de l\'image');
    }
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="h4 mb-0">Produits</h2>
        <button 
          className="btn btn-primary"
          onClick={() => window.$('#addProductModal').modal('show')}
        >
          Ajouter un produit
        </button>
      </div>

      <div className="card">
        <div className="card-body">
          {products.length === 0 ? (
            <p className="text-center text-muted my-5">Aucun produit pour le moment</p>
          ) : (
            <>
              <ProductTable 
                products={products}
                onEdit={handleEditClick}
                onDelete={handleDeleteProduct}
              />
              <Pagination />
            </>
          )}
        </div>
      </div>

      <AddProductModal
        newProduct={newProduct}
        setNewProduct={setNewProduct}
        categories={categories}
        onSubmit={handleAddProduct}
        onImageUpload={handleImageUpload}
      />
      
      <EditProductModal
        editingProduct={editingProduct}
        setEditingProduct={setEditingProduct}
        categories={categories}
        onSubmit={handleUpdateProduct}
        onImageUpload={handleImageUpload}
        onImageDelete={handleImageDelete}
      />
    </div>
  );
};

export default Products;